import React, { useState, useEffect } from 'react';
import { risorseAPI, ruoliAPI } from '../lib/api';
import { formatCurrency, getStatusLabel } from '../lib/utils';
import { StatusBadge } from '../components/common/StatusBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Users,
  Loader2
} from 'lucide-react';

const tipoRisorsaOptions = [
  { value: 'dipendente', label: 'Dipendente' },
  { value: 'freelance', label: 'Freelance' },
  { value: 'consulente', label: 'Consulente' },
];

const initialFormData = {
  nome: '',
  cognome: '',
  email: '',
  ruolo_id: '',
  costo_orario: 0,
  tariffa_standard: 0,
  dipartimento: '',
  tipo: 'dipendente',
  is_active: true
};

export const RisorsePage = () => {
  const [risorse, setRisorse] = useState([]);
  const [ruoli, setRuoli] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRisorsa, setEditingRisorsa] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [risorseRes, ruoliRes] = await Promise.all([
        risorseAPI.getAll(),
        ruoliAPI.getAll()
      ]);
      setRisorse(risorseRes.data);
      setRuoli(ruoliRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setIsLoading(false);
    }
  };

  const getRuoloName = (ruoloId) => {
    const ruolo = ruoli.find(r => r.id === ruoloId);
    return ruolo?.nome || '-';
  };

  const handleOpenDialog = (risorsa = null) => {
    if (risorsa) {
      setEditingRisorsa(risorsa);
      setFormData({
        nome: risorsa.nome || '',
        cognome: risorsa.cognome || '',
        email: risorsa.email || '',
        ruolo_id: risorsa.ruolo_id || '',
        costo_orario: risorsa.costo_orario || 0,
        tariffa_standard: risorsa.tariffa_standard || 0,
        dipartimento: risorsa.dipartimento || '',
        tipo: risorsa.tipo || 'dipendente',
        is_active: risorsa.is_active !== false
      });
    } else {
      setEditingRisorsa(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingRisorsa(null);
    setFormData(initialFormData);
  };

  const handleRuoloChange = (ruoloId) => {
    const ruolo = ruoli.find(r => r.id === ruoloId);
    setFormData({
      ...formData,
      ruolo_id: ruoloId,
      costo_orario: ruolo?.costo_standard || formData.costo_orario,
      tariffa_standard: ruolo?.tariffa_standard || formData.tariffa_standard
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submitData = {
      ...formData,
      costo_orario: parseFloat(formData.costo_orario) || 0,
      tariffa_standard: parseFloat(formData.tariffa_standard) || 0,
      ruolo_id: formData.ruolo_id || null
    };

    try {
      if (editingRisorsa) {
        await risorseAPI.update(editingRisorsa.id, submitData);
        toast.success('Risorsa aggiornata con successo');
      } else {
        await risorseAPI.create(submitData);
        toast.success('Risorsa creata con successo');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving risorsa:', error);
      toast.error('Errore nel salvataggio della risorsa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (risorsa) => {
    if (window.confirm(`Sei sicuro di voler eliminare "${risorsa.nome} ${risorsa.cognome}"?`)) {
      try {
        await risorseAPI.delete(risorsa.id);
        toast.success('Risorsa eliminata con successo');
        loadData();
      } catch (error) {
        console.error('Error deleting risorsa:', error);
        toast.error('Errore nell\'eliminazione della risorsa');
      }
    }
  };

  const filteredRisorse = risorse.filter(risorsa =>
    `${risorsa.nome} ${risorsa.cognome}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    risorsa.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (risorsa.dipartimento && risorsa.dipartimento.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="risorse-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risorse-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Risorse</h1>
          <p className="text-muted-foreground">Gestisci le risorse aziendali</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-risorsa-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuova Risorsa
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca risorse..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-risorse-input"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Dipartimento</TableHead>
                <TableHead className="text-right">Costo/h</TableHead>
                <TableHead className="text-right">Tariffa/h</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRisorse.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nessuna risorsa trovata</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRisorse.map((risorsa) => (
                  <TableRow key={risorsa.id} data-testid={`risorsa-row-${risorsa.id}`}>
                    <TableCell className="font-medium">{risorsa.nome} {risorsa.cognome}</TableCell>
                    <TableCell>{risorsa.email}</TableCell>
                    <TableCell>{getRuoloName(risorsa.ruolo_id)}</TableCell>
                    <TableCell>
                      <StatusBadge status={risorsa.tipo} />
                    </TableCell>
                    <TableCell>{risorsa.dipartimento || '-'}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(risorsa.costo_orario)}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(risorsa.tariffa_standard)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`risorsa-actions-${risorsa.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(risorsa)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(risorsa)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="risorsa-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingRisorsa ? 'Modifica Risorsa' : 'Nuova Risorsa'}
            </DialogTitle>
            <DialogDescription>
              {editingRisorsa ? 'Modifica i dati della risorsa' : 'Inserisci i dati della nuova risorsa'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                  data-testid="risorsa-nome-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={formData.cognome}
                  onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                  required
                  data-testid="risorsa-cognome-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="risorsa-email-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ruolo">Ruolo</Label>
                <Select value={formData.ruolo_id || "none"} onValueChange={(v) => handleRuoloChange(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="risorsa-ruolo-select">
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun ruolo</SelectItem>
                    {ruoli.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
                  <SelectTrigger data-testid="risorsa-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tipoRisorsaOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dipartimento">Dipartimento</Label>
              <Input
                id="dipartimento"
                value={formData.dipartimento}
                onChange={(e) => setFormData({...formData, dipartimento: e.target.value})}
                placeholder="Es. IT, Sales, Operations..."
                data-testid="risorsa-dipartimento-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="costo_orario">Costo Orario (€)</Label>
                <Input
                  id="costo_orario"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.costo_orario}
                  onChange={(e) => setFormData({...formData, costo_orario: e.target.value})}
                  data-testid="risorsa-costo-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tariffa_standard">Tariffa Standard (€)</Label>
                <Input
                  id="tariffa_standard"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.tariffa_standard}
                  onChange={(e) => setFormData({...formData, tariffa_standard: e.target.value})}
                  data-testid="risorsa-tariffa-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="risorsa-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingRisorsa ? 'Salva Modifiche' : 'Crea Risorsa'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RisorsePage;
